/**
 * Global fetch interceptor for transparent 401 → token refresh → retry.
 *
 * Wraps window.fetch so that any API request returning 401 automatically
 * triggers a token refresh and retries the original request once. All
 * existing fetch call sites are covered without migration.
 *
 * @module
 * @see docs/api/auth.md
 */

import * as Sentry from '@sentry/react';
import { WEB_CLIENT_HEADER, WEB_CLIENT_HEADER_VALUE } from '@chatsdet/types';
import { API_URL } from '../config';
import { attemptTokenRefresh, setOriginalFetch } from './auth-refresh';
import {
  EXPECTED_ORG_HEADER,
  ORGANIZATION_CHANGED_CODE,
  getRenderedOrganizationId,
  isOrgExpectationExempt,
  reloadForOrganizationChange,
} from './organization-context';

function resolveUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.toString();
  return input.url;
}

let installed = false;

function isApiUrl(url: URL): boolean {
  const api = new URL(API_URL, window.location.href);
  const basePath = api.pathname.replace(/\/+$/, '');
  return (
    url.origin === api.origin &&
    (!basePath || url.pathname === basePath || url.pathname.startsWith(`${basePath}/`))
  );
}

/** Mark API requests as coming from the web app so the API can apply web-client rules. */
export function withWebClientHeader(
  input: RequestInfo | URL,
  init?: RequestInit
): RequestInit | undefined {
  const url = resolveRequestUrl(input);
  if (!url || !isApiUrl(url)) return init;
  const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
  headers.set(WEB_CLIENT_HEADER, WEB_CLIENT_HEADER_VALUE);
  return { ...init, headers };
}

/**
 * Tell the API which org this tab rendered, so it can refuse (409) instead of
 * writing into a different org after another tab switched the session cookie.
 * An expectation only: the API never switches orgs because of it.
 */
export function withExpectedOrgHeader(
  input: RequestInfo | URL,
  init?: RequestInit
): RequestInit | undefined {
  const orgId = getRenderedOrganizationId();
  if (!orgId) return init;
  const url = resolveRequestUrl(input);
  if (!url || !isApiUrl(url) || isOrgExpectationExempt(url.pathname)) return init;
  const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
  if (headers.has(EXPECTED_ORG_HEADER)) return init;
  headers.set(EXPECTED_ORG_HEADER, orgId);
  return { ...init, headers };
}

/** On a 409 ORGANIZATION_CHANGED response, reload so the page renders the cookie's org. */
export async function handleOrganizationChanged(response: Response): Promise<void> {
  if (response.status !== 409) return;
  const body = await response
    .clone()
    .json()
    .catch(() => null);
  if (body?.code === ORGANIZATION_CHANGED_CODE) reloadForOrganizationChange();
}

type FetchFailureBreadcrumbData = {
  url_host?: string;
  url_origin?: string;
  url_path?: string;
  url_query_present?: boolean;
  method: string;
  api_request: boolean;
  elapsed_ms: number;
  error_name?: string;
  error_message?: string;
  document_visibility?: DocumentVisibilityState;
  navigator_online?: boolean;
  request_aborted?: boolean;
  resource_timing_status?: 'available' | 'unavailable';
  resource_timing_dns_ms?: number;
  resource_timing_connect_ms?: number;
  resource_timing_request_ms?: number;
  resource_timing_response_ms?: number;
  network_connection_status?: 'supported' | 'unsupported';
  network_effective_type?: string;
  network_rtt_ms?: number;
  network_downlink_mbps?: number;
};

type NetworkConnectionSnapshot = {
  effectiveType?: string;
  rtt?: number;
  downlink?: number;
};

function getNetworkConnectionData() {
  const connection = (navigator as Navigator & { connection?: NetworkConnectionSnapshot })
    .connection;
  if (!connection) return { network_connection_status: 'unsupported' as const };

  return {
    network_connection_status: 'supported' as const,
    network_effective_type: connection.effectiveType,
    network_rtt_ms: connection.rtt,
    network_downlink_mbps: connection.downlink,
  };
}

function getResourceTimingData(url: URL, startedAt: number, endedAt: number) {
  const entry = performance
    .getEntriesByName(url.href, 'resource')
    .filter(
      (candidate): candidate is PerformanceResourceTiming =>
        candidate.entryType === 'resource' &&
        candidate.startTime >= startedAt &&
        candidate.startTime <= endedAt
    )
    .at(-1);

  if (!entry) return { resource_timing_status: 'unavailable' as const };

  const duration = (start: number, end: number) =>
    start > 0 && end >= start ? Math.round(end - start) : undefined;

  const phases = {
    resource_timing_dns_ms: duration(entry.domainLookupStart, entry.domainLookupEnd),
    resource_timing_connect_ms: duration(entry.connectStart, entry.connectEnd),
    resource_timing_request_ms: duration(entry.requestStart, entry.responseStart),
    resource_timing_response_ms: duration(entry.responseStart, entry.responseEnd),
  };

  if (Object.values(phases).every((phase) => phase === undefined)) {
    return { resource_timing_status: 'unavailable' as const };
  }

  return {
    resource_timing_status: 'available' as const,
    ...phases,
  };
}

function resolveMethod(input: RequestInfo | URL, init?: RequestInit): string {
  if (init?.method) return init.method.toUpperCase();
  if (input instanceof Request) return input.method.toUpperCase();
  return 'GET';
}

function resolveRequestUrl(input: RequestInfo | URL): URL | null {
  const url = resolveUrl(input);
  try {
    return new URL(url, window.location.href);
  } catch {
    return null;
  }
}

export function buildFetchFailureBreadcrumbData(params: {
  input: RequestInfo | URL;
  init?: RequestInit;
  error: unknown;
  elapsedMs: number;
  startedAt?: number;
  endedAt?: number;
}): FetchFailureBreadcrumbData {
  const parsedUrl = resolveRequestUrl(params.input);
  const error = params.error instanceof Error ? params.error : null;

  const isApiRequest = parsedUrl ? parsedUrl.href.startsWith(API_URL) : false;
  return {
    url_host: parsedUrl?.host,
    url_origin: parsedUrl?.origin,
    url_path: parsedUrl?.pathname,
    url_query_present: parsedUrl ? parsedUrl.search.length > 0 : undefined,
    method: resolveMethod(params.input, params.init),
    api_request: isApiRequest,
    elapsed_ms: Math.round(params.elapsedMs),
    error_name: error?.name,
    error_message: error?.message,
    document_visibility: document.visibilityState,
    navigator_online: navigator.onLine,
    request_aborted: params.init?.signal?.aborted,
    ...getNetworkConnectionData(),
    ...(isApiRequest && params.startedAt !== undefined && params.endedAt !== undefined
      ? getResourceTimingData(parsedUrl!, params.startedAt, params.endedAt)
      : {}),
  };
}

function addFetchFailureBreadcrumb(params: {
  input: RequestInfo | URL;
  init?: RequestInit;
  error: unknown;
  elapsedMs: number;
  startedAt: number;
  endedAt: number;
}): void {
  Sentry.addBreadcrumb({
    category: 'fetch.transport',
    level: 'error',
    message: 'fetch transport failure',
    data: buildFetchFailureBreadcrumbData(params),
  });
}

/**
 * Install the global fetch interceptor.
 * Must be called once at app startup, before any fetch calls are made.
 */
export function installFetchInterceptor(): void {
  if (installed) return;
  installed = true;

  const nativeFetch = window.fetch.bind(window) as typeof window.fetch;
  setOriginalFetch(nativeFetch);

  const interceptedFetch = async (
    input: RequestInfo | URL,
    init?: RequestInit
  ): Promise<Response> => {
    const startedAt = performance.now();
    let response: Response;
    const requestInit = withExpectedOrgHeader(input, withWebClientHeader(input, init));
    try {
      response = await nativeFetch(input, requestInit);
    } catch (error) {
      const endedAt = performance.now();
      addFetchFailureBreadcrumb({
        input,
        init,
        error,
        elapsedMs: endedAt - startedAt,
        startedAt,
        endedAt,
      });
      throw error;
    }

    if (response.status === 409) {
      await handleOrganizationChanged(response);
      return response;
    }

    if (response.status !== 401) {
      return response;
    }

    const url = resolveUrl(input);

    // Only intercept our own API requests
    if (!url.startsWith(API_URL)) {
      return response;
    }

    // Never intercept auth endpoints — a 401 from /auth/signout, /auth/email/signin,
    // etc. means "bad credentials" or "already signed out", not "expired token".
    // This also prevents infinite loops on /auth/refresh itself.
    if (url.includes('/auth/')) {
      return response;
    }

    const refreshed = await attemptTokenRefresh();
    if (!refreshed) {
      return response;
    }

    // Retry the original request once with the fresh token
    return nativeFetch(input, requestInit);
  };

  // Preserve static methods (e.g. fetch.preconnect) while replacing the callable
  window.fetch = Object.assign(interceptedFetch, nativeFetch) as typeof window.fetch;
}
